// Pure helpers for the AI assistant: text parsing, result cards and action labels.

export const textBlocks = (text, role) => {
  if (role !== 'assistant') {
    return [{ type: 'plain', text: text || '' }];
  }

  return String(text || '')
    .split('\n')
    .map((line) => line.trim())
    .map((line) => {
      if (!line) return { type: 'space', text: '' };

      const numbered = line.match(/^(\d+)\.\s+(.*)$/);
      if (numbered) return { type: 'numbered', number: numbered[1], text: numbered[2] };

      const bullet = line.match(/^[-*]\s+(.*)$/);
      if (bullet) return { type: 'bullet', text: bullet[1] };

      const markdownHeading = line.match(/^#{1,3}\s+(.*)$/);
      if (markdownHeading) return { type: 'heading', text: markdownHeading[1] };

      const boldHeading = line.match(/^\*\*(.+?)\*\*:?\s*$/);
      if (boldHeading) return { type: 'heading', text: boldHeading[1] };

      return { type: 'plain', text: line };
    });
};

export const inlineParts = (text) =>
  String(text || '')
    .split(/(\*\*[^*]+\*\*)/g)
    .filter((part) => part !== '')
    .map((part) => {
      const bold = part.match(/^\*\*([^*]+)\*\*$/);
      return { text: bold ? bold[1] : part, strong: Boolean(bold) };
    });

// Plain text without markdown markers, used for "copy to clipboard".
export const plainText = (text) => String(text || '').replace(/\*\*([^*]+)\*\*/g, '$1');

const parseToolResult = (toolResult) => {
  const jsonStart = toolResult.indexOf('{');
  if (jsonStart === -1) return null;
  try {
    return JSON.parse(toolResult.slice(jsonStart));
  } catch (error) {
    return null;
  }
};

const invoiceStatus = (invoice) => {
  if (invoice.totalUnPaidAmountAsFloat > 0) {
    return { label: `Reste ${invoice.totalUnPaidAmount || '-'}`, tone: 'warning' };
  }
  if (invoice.billingStatus === 1) {
    return { label: 'Payée', tone: 'success' };
  }
  return { label: 'Facture vente', tone: 'neutral' };
};

const invoiceToCard = (invoice, index) => {
  const reference = invoice.refNumber || invoice.specialRef || `Facture ${index + 1}`;
  const status = invoiceStatus(invoice);
  return {
    key: invoice.id || invoice.specialRef || `${reference}-${index}`,
    icon: 'fas fa-file-invoice',
    title: reference,
    subtitle: invoice.customer?.companyName || invoice.customer?.name || 'Client non défini',
    amount: invoice.totalTTC || '-',
    date: invoice.submissionDateWithoutTime || invoice.submissionDate || invoice.creationDate || '-',
    status: status.label,
    tone: status.tone,
    route: invoice.specialRef ? `/document/generate/invoice/${encodeURIComponent(invoice.specialRef)}` : null,
    actionLabel: 'Voir la facture',
  };
};

const customerToCard = (customer, index) => {
  const title = customer.companyName || customer.name || `Client ${index + 1}`;
  const address = [customer.address, customer.postalCode, customer.city].filter(Boolean).join(', ');
  return {
    key: customer.id || customer.code || `${title}-${index}`,
    icon: 'fas fa-user-tie',
    title,
    subtitle: address || 'Adresse non renseignée',
    amount: customer.code || '',
    date: customer.taxNumber ? `MF: ${customer.taxNumber}` : 'Matricule fiscal non renseigné',
    status: customer.phone || customer.creationDate || '',
    tone: 'neutral',
    route: null,
    actionLabel: '',
  };
};

const genericToCard = (item, index, toolName) => {
  const title = item.companyName || item.name || item.refNumber || item.description || item.title
    || `${toolName || 'Résultat'} ${index + 1}`;
  return {
    key: item.id || item.specialRef || `${title}-${index}`,
    icon: 'fas fa-layer-group',
    title,
    subtitle: item.address || item.note || item.projectName || item.category || item.creationDate || '',
    amount: item.totalTTC || item.amount || item.code || '',
    date: item.date || item.creationDate || item.submissionDate || '',
    status: item.status || item.documentStatus || '',
    tone: 'neutral',
    route: null,
    actionLabel: '',
  };
};

export const buildCards = (response) => {
  if (!response || !response.toolResult) return [];

  const parsed = parseToolResult(response.toolResult);
  if (!parsed || !Array.isArray(parsed.content)) return [];

  const toolName = response.toolName || '';
  if (toolName.indexOf('invoice_') === 0) return parsed.content.map(invoiceToCard);
  if (toolName.indexOf('customer_') === 0) return parsed.content.map(customerToCard);
  return parsed.content.map((item, index) => genericToCard(item, index, toolName));
};

export const buildAssistantText = (response, cards) => {
  if (response.answer) return response.answer;
  if (cards.length) return `${cards.length} résultat(s) trouvé(s).`;
  return 'Je n’ai pas reçu de réponse exploitable.';
};

export const errorMessage = (error) =>
  error?.response?.data?.message || 'Impossible de contacter l’assistant pour le moment.';

const TOOLS_REQUIRING_AMOUNT = [
  'assistant_create_purchase_invoice',
  'assistant_create_sales_invoice',
  'assistant_create_sales_offer',
];

export const isValidConfirmation = (toolName, args = {}) => {
  if (!TOOLS_REQUIRING_AMOUNT.includes(toolName)) return true;
  if (Array.isArray(args.lineItems) && args.lineItems.length) {
    return args.lineItems.some((item) => {
      const amount = Number(item.amountHt || item.priceHt || item.unitPriceHt || item.unitPrice || item.price);
      return Number.isFinite(amount) && amount > 0;
    });
  }
  const amount = Number(args.amountHt);
  return Number.isFinite(amount) && amount > 0;
};

const TOOL_LABELS = {
  assistant_create_sales_offer: 'Créer une offre',
  assistant_create_sales_invoice: 'Créer une facture de vente',
  assistant_create_purchase_invoice: 'Créer une facture d’achat',
  assistant_create_purchasing_supplier: 'Créer un fournisseur achat',
  assistant_convert_offer_to_invoice: 'Convertir une offre en facture',
  assistant_convert_work_tracking_sheet_to_invoice: 'Convertir une fiche de suivi en facture',
  assistant_update_and_submit_invoice: 'Corriger et valider une facture',
  invoice_submit_invoice: 'Valider une facture',
  invoice_remove_invoice: 'Supprimer une facture',
  invoice_mark_invoice_paid: 'Marquer une facture comme payée',
  invoice_add_partial_payment: 'Ajouter un paiement partiel',
  order_submit_order: 'Valider une offre',
  purchasing_invoice_submit_invoice: 'Valider une facture d’achat',
  credit_note_submit: 'Valider un avoir',
  expense_save_expense: 'Enregistrer une dépense',
  employee_pay_employee: 'Payer un employé',
  employee_pay_all_employees: 'Payer tous les employés',
  stock_validate_inventory: 'Valider un inventaire',
};

export const toolLabel = (toolName) => TOOL_LABELS[toolName] || 'Exécuter une action métier';

// Destructive actions get a red confirmation button instead of the default one.
export const isDestructiveTool = (toolName) => /remove|delete/.test(toolName || '');

const SUMMARY_LABELS = {
  customerName: 'Client',
  purchasingSupplierName: 'Fournisseur',
  orderSpecialRef: 'Offre',
  invoiceReference: 'Facture',
  invoiceExtRef: 'Référence fournisseur',
  invoiceExtDate: 'Date facture',
  workTrackingSheetSpecialRef: 'Fiche de suivi',
  amountHt: 'Montant HT',
  lineItems: 'Lignes',
  overrideAmountHt: 'Montant HT',
  vatRate: 'TVA',
  designation: 'Désignation',
  overrideDesignation: 'Désignation',
  projectName: 'Projet',
  note: 'Note',
};

const summaryValue = (key, value) => {
  if (key === 'vatRate') return `${value}%`;
  if (key === 'lineItems' && Array.isArray(value)) return `${value.length} ligne(s)`;
  return String(value);
};

export const confirmationSummary = (args = {}) =>
  Object.keys(SUMMARY_LABELS)
    .filter((key) => args[key] !== undefined && args[key] !== null && args[key] !== '')
    .map((key) => ({ label: SUMMARY_LABELS[key], value: summaryValue(key, args[key]) }))
    .slice(0, 6);

export const formatTime = (timestamp) => {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

export const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};
